'use client';

import React, { useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Sparkles, CarFront, Users, Briefcase, Navigation } from 'lucide-react';

export default function CarFitAdvisorPage() {
    const [budget, setBudget] = useState<number[]>([50000]);
    const [familySize, setFamilySize] = useState('1');
    const [purpose, setPurpose] = useState('daily');
    const [extraInfo, setExtraInfo] = useState('');

    const [isLoading, setIsLoading] = useState(false);
    const [recommendations, setRecommendations] = useState<any[] | null>(null);

    const handleAnalyze = async () => {
        setIsLoading(true);
        setRecommendations(null);

        try {
            const BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
            const res = await fetch(`${BASE_URL}/api/ai/advise-fit/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    preferences: {
                        budget: budget[0],
                        familySize,
                        purpose,
                        extraInfo
                    }
                })
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'فشل الاتصال بالمستشار الذكي');

            setRecommendations(data.recommendations || []);
        } catch (error: any) {
            alert(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container py-12 max-w-4xl mx-auto px-4">
            <div className="mb-10 text-center space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h1 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent inline-flex items-center gap-3">
                    <Sparkles className="w-10 h-10 text-blue-600" />
                    المستشار الذكي
                </h1>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                    أجب على بعض الأسئلة وسيقوم الذكاء الاصطناعي باقتراح أفضل السيارات التي تتناسب مع أسلوب حياتك وميزانيتك.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                <Card className="rounded-[2.5rem] border-none shadow-xl bg-white animate-in zoom-in-95 duration-500">
                    <CardHeader>
                        <CardTitle className="text-2xl font-black">حدد احتياجاتك</CardTitle>
                        <CardDescription className="text-base text-gray-500">
                            ساعدنا على فهم ما تبحث عنه بالضبط.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">

                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <Label className="text-base font-bold flex items-center gap-2">
                                    <Briefcase className="w-5 h-5 text-blue-500" /> الميزانية التقديرية (ريال)
                                </Label>
                                <span className="font-black text-blue-600 text-lg">{budget[0].toLocaleString()}</span>
                            </div>
                            <Slider
                                value={budget}
                                onValueChange={setBudget}
                                max={500000}
                                step={5000}
                                className="py-4"
                            />
                        </div>

                        <div className="space-y-3">
                            <Label className="text-base font-bold flex items-center gap-2">
                                <Users className="w-5 h-5 text-blue-500" /> عدد أفراد العائلة
                            </Label>
                            <Select value={familySize} onValueChange={setFamilySize}>
                                <SelectTrigger className="h-14 rounded-2xl">
                                    <SelectValue placeholder="اختر العدد" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">شخص واحد</SelectItem>
                                    <SelectItem value="2">2 أشخاص</SelectItem>
                                    <SelectItem value="4">3 - 4 أشخاص</SelectItem>
                                    <SelectItem value="5">5 وأكثر (سيارة عائلية)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-3">
                            <Label className="text-base font-bold flex items-center gap-2">
                                <CarFront className="w-5 h-5 text-blue-500" /> الغرض الأساسي
                            </Label>
                            <Select value={purpose} onValueChange={setPurpose}>
                                <SelectTrigger className="h-14 rounded-2xl">
                                    <SelectValue placeholder="اختر الغرض" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="daily">مشاوير يومية (مدينة)</SelectItem>
                                    <SelectItem value="travel">سفر وخطوط طويلة</SelectItem>
                                    <SelectItem value="offroad">بر ورحلات (دفع رباعي)</SelectItem>
                                    <SelectItem value="luxury">فخامة ومناسبات</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-3">
                            <Label className="text-base font-bold">ملاحظات إضافية</Label>
                            <Textarea
                                placeholder="مثلاً: أفضل السيارات اليابانية، أو تهمني كفاءة استهلاك الوقود..."
                                className="resize-none h-24 rounded-2xl"
                                value={extraInfo}
                                onChange={(e) => setExtraInfo(e.target.value)}
                            />
                        </div>

                        <Button
                            className="w-full h-14 rounded-2xl text-lg font-bold shadow-lg"
                            onClick={handleAnalyze}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="animate-pulse">جاري التحليل...</span>
                            ) : (
                                <>الحصول على التوصيات <Sparkles className="ml-2 w-5 h-5" /></>
                            )}
                        </Button>

                    </CardContent>
                </Card>

                <div className="space-y-6">
                    {!recommendations && !isLoading && (
                        <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-8 bg-blue-50/50 rounded-[2.5rem] border border-blue-100/50 border-dashed animate-in fade-in">
                            <Navigation className="w-16 h-16 text-blue-200 mb-4" />
                            <h3 className="text-xl font-bold text-blue-900 mb-2">في انتظار إدخالاتك</h3>
                            <p className="text-blue-700/60 max-w-sm">
                                سيقوم الذكاء الاصطناعي بربط متطلباتك مع قاعدة بيانات السيارات لتقديم أفضل التوصيات المخصصة لك.
                            </p>
                        </div>
                    )}

                    {isLoading && (
                        <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-8 bg-blue-50/50 rounded-[2.5rem] animate-pulse">
                            <Sparkles className="w-16 h-16 text-blue-400 animate-spin mb-4" />
                            <h3 className="text-xl font-bold text-blue-900 mb-2">جاري البحث في السيارات...</h3>
                            <p className="text-blue-700/60 max-w-sm">
                                الرجاء الانتظار بينما نقوم بمطابقة أسلوب حياتك مع أفضل الخيارات.
                            </p>
                        </div>
                    )}

                    {recommendations && recommendations.length > 0 && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-right-8 duration-500">
                            <h3 className="text-2xl font-black text-gray-900 mb-6 flex items-center gap-2">
                                <Sparkles className="w-6 h-6 text-yellow-500" />
                                أفضل التوصيات لك
                            </h3>

                            {recommendations.map((rec, i) => (
                                <Card key={i} className="rounded-3xl border-none shadow-md overflow-hidden bg-white hover:scale-[1.02] transition-transform cursor-pointer">
                                    <div className="bg-gradient-to-l from-blue-50 to-transparent p-6">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{rec.make}</span>
                                                <h4 className="text-xl font-black text-gray-900">{rec.model}</h4>
                                            </div>
                                            <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center text-xl font-black text-blue-600">
                                                #{i + 1}
                                            </div>
                                        </div>
                                        <p className="mt-4 text-gray-600 leading-relaxed text-sm">
                                            {rec.reason}
                                        </p>

                                        <Button variant="link" className="px-0 mt-4 text-blue-600 font-bold" onClick={() => window.location.href = `/buy/search?query=${rec.make} ${rec.model}`}>
                                            البحث عن هذه السيارة في منصتنا &rarr;
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}

                    {recommendations?.length === 0 && (
                        <div className="p-8 text-center bg-red-50 rounded-[2.5rem] text-red-900">
                            <p className="font-bold">لم نتمكن من العثور على توصيات دقيقة. حاول تغيير بعض التفضيلات.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
